/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { domUtils, propsUtils, keyboardUtils } from '@ba-ui-toolkit/utils';
import TooltipPopup from './TooltipPopup.js';
import styles from './Tooltip.scss';

const tooltipClassName = styles.tooltipClassName.replace(/"/g, '');
const { sanitize } = propsUtils;
const { isKey, keyNames } = keyboardUtils;

const DEFAULT_DELAY_TIME = 800;
export default class Tooltip extends Component {
	static propTypes = {
		display: PropTypes.oneOf(['block', 'inline-block']),
		orient: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
		children: PropTypes.node,
		title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		delay: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
		disabled: PropTypes.bool,
		className: PropTypes.string,
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		style: PropTypes.object,
		/** Specifies the width of the tooltip popup */
		popupWidth: PropTypes.string,
	};

	static defaultProps = {
		orient: 'bottom',
		display: 'inline-block',
		className: '',
		style: {},
	};

	constructor() {
		super();
		this.handleHashChange = this.handleHashChange.bind(this);
	}

	state = {
		top: 0,
		left: 0,
		width: 0,
		height: 0,
		hover: false,
	};

	handleHashChange() {
		this.setState(
			{
				hover: false,
			},
			this._clearTimer
		);
	}

	componentDidMount() {
		this._resetStates();
		window.addEventListener('hashchange', this.handleHashChange);
	}

	componentWillUnmount() {
		this._clearTimer();
		window.removeEventListener('hashchange', this.handleHashChange);
	}

	_setTimer() {
		const { delay } = this.props;

		let time;

		if (typeof delay === 'number') {
			time = delay;
		} else {
			time = DEFAULT_DELAY_TIME;
		}

		this.timer = setTimeout(() => {
			this.setState({
				hidden: false,
			});
		}, time);
	}
	_clearTimer() {
		const { delay } = this.props;

		document.removeEventListener('keydown', this._onKeyDown, false);
		if (delay && this.timer) {
			clearTimeout(this.timer);
		}
	}
	_resetStates() {
		this.setState({
			top: domUtils.computeTopWith(this.refs.trigger),
			left: domUtils.computeLeftWith(this.refs.trigger),
			width: this.refs.trigger.clientWidth,
			height: this.refs.trigger.clientHeight,
			trigger: this.refs.trigger,
			availableSpace: domUtils.availableSpace(this.refs.trigger),
		});
	}

	_onKeyDown = e => {
		isKey(e, keyNames.ESCAPE) && this.setState({ hover: false }, this._clearTimer);
	};

	_onMouseEnter = () => {
		this._resetStates();
		const { delay } = this.props;
		const newState = {
			hover: true,
			...(delay ? { hidden: true } : {}),
		};
		document.addEventListener('keydown', this._onKeyDown, false);
		this.setState(newState, delay && this._setTimer);
	};

	_onClick = e => {
		const elementOnMouse =
			document.elementFromPoint && document.elementFromPoint(e.clientX, e.clientY);

		if (
			elementOnMouse &&
			this.refs.trigger !== elementOnMouse &&
			this.refs.trigger !== elementOnMouse.parentNode
		) {
			this.setState({ hover: false }, this._clearTimer);
		}
	};

	_onMouseLeave = () => {
		this.setState({ hover: false }, this._clearTimer);
	};
	render() {
		// update orient by calling autoposition
		const {
			display,
			orient,
			title,
			children,
			className,
			style,
			disabled,
			popupWidth,
			textDir,
			...others
		} = this.props;

		return (
			<div
				{...sanitize(others, Tooltip)}
				ref="trigger"
				className={`${tooltipClassName} ${className}`}
				style={{
					display,
					...style,
				}}
				onMouseEnter={this._onMouseEnter}
				onMouseLeave={this._onMouseLeave}
				onClick={this._onClick}
			>
				{children}
				{title != null &&
					title !== '' &&
					this.state.hover &&
					!this.state.hidden &&
					!disabled && (
						<TooltipPopup
							orient={orient}
							title={title}
							textDir={textDir}
							availableSpace={this.state.availableSpace}
							triggerState={this.state}
							popupWidth={popupWidth}
						/>
					)}
			</div>
		);
	}
}
